import { useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import {
  Button,
  TextField,
  theme as defaultTheme,
  useSnackbar,
} from "@mindlercare/ui-web";
import { Typography } from "components/typography";
import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useQuery } = ReactQueryV5;
import { get, post } from "../../../services/httpClient";
import { useDemo } from "contexts/DemoContext";
import { useApplicationMarket } from "core/selectors/marketSelectors";

const ATTRIBUTE_NAME = "AI_ICBT_FEEDBACK_INSTRUCTIONS";
const MAX_LENGTH = 1000;

type UserAttribute = {
  attribute: string;
  value: string;
};

type InstructionsMap = Record<string, string>;

const parseInstructions = (
  raw: string,
  defaultLangCode: string,
): InstructionsMap => {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as InstructionsMap;
    }
  } catch {
    // legacy plain string — migrate to default language
    if (raw.trim()) return { [defaultLangCode]: raw };
  }
  return {};
};

export const AiPreferences = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const isSensitiveDataHidden = useDemo();
  const [editedInstructions, setEditedInstructions] =
    useState<InstructionsMap | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const activeCountry = useApplicationMarket();

  const { data: response, refetch } = useQuery({
    queryKey: ["user-attributes"],
    queryFn: () => get<{ data: UserAttribute[] }>("user-attributes"),
  });

  const savedValue =
    response?.data?.find((attr) => attr.attribute === ATTRIBUTE_NAME)?.value ??
    "";
  const defaultLangCode =
    activeCountry.languages?.find(
      (l) => l.languageId === activeCountry.defaultLanguageId,
    )?.codeISO2 ?? "";
  const savedInstructions = parseInstructions(savedValue, defaultLangCode);
  const instructions = editedInstructions ?? savedInstructions;

  const handleChange = (langCode: string, value: string) => {
    setEditedInstructions({
      ...(editedInstructions ?? savedInstructions),
      [langCode]: value,
    });
  };

  const hasChanges =
    editedInstructions !== null &&
    JSON.stringify(editedInstructions) !== JSON.stringify(savedInstructions);

  const handleCancel = () => {
    setEditedInstructions(null);
  };

  const handleSave = async () => {
    if (!hasChanges) return;

    setIsSaving(true);
    try {
      await post("user-attributes", {
        attributes: [
          // API rejects empty strings, so send a single space to clear the value
          {
            attribute: ATTRIBUTE_NAME,
            value: JSON.stringify(instructions),
          },
        ],
      });
      await refetch();
      setEditedInstructions(null);
    } catch {
      enqueueSnackbar({
        text: t("API_ERROR_GENERIC"),
        isError: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const languages = activeCountry.languages ?? [];

  return (
    <Container>
      <SectionHeader>
        <Typography variant="h3" insensitive>
          {t("LLM_AI_FEEDBACK.PREFERENCES_TITLE")}
        </Typography>
        <Typography
          variant="body"
          color="GRAY_DARK"
          sx={{ lineHeight: 1.4, margin: 0 }}
          insensitive
        >
          {t("LLM_AI_FEEDBACK.PREFERENCES_INSTRUCTIONS_DESCRIPTION")}
        </Typography>
      </SectionHeader>
      {languages.map((lang) => {
        const value = instructions[lang.codeISO2] ?? "";
        return (
          <InputGroup key={lang.codeISO2}>
            <TextField
              multiline
              label={`${t("LLM_AI_FEEDBACK.PREFERENCES_INSTRUCTIONS_LABEL")} (${lang.name})`}
              placeholder={t(
                "LLM_AI_FEEDBACK.PREFERENCES_INSTRUCTIONS_PLACEHOLDER",
              )}
              value={value}
              onChange={(e) => handleChange(lang.codeISO2, e.target.value)}
              maxlength={MAX_LENGTH}
              disabled={isSaving}
              width="full"
              hideSensitiveData={isSensitiveDataHidden}
              sensitiveField={true}
            />
            <CharacterCount $atLimit={value.length >= MAX_LENGTH}>
              {value.length}/{MAX_LENGTH}
            </CharacterCount>
          </InputGroup>
        );
      })}
      <ButtonGroup>
        <Button
          variant="light"
          onClick={handleCancel}
          disabled={!hasChanges || isSaving}
        >
          {t("CANCEL")}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
        >
          {t("SAVE")}
        </Button>
      </ButtonGroup>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${defaultTheme.SPACING.XL};
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${defaultTheme.SPACING.SM};
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 60%;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${defaultTheme.SPACING.SM};
  justify-content: flex-end;
`;

const CharacterCount = styled.span<{ $atLimit: boolean }>`
  align-self: flex-end;
  font-size: ${defaultTheme.FONT.SIZE.SMALL};
  color: ${({ $atLimit }) =>
    $atLimit ? defaultTheme.COLORS.ERROR : defaultTheme.COLORS.GRAY_DARK};
`;
